import Vue from 'vue'
import gql from 'graphql-tag'
import { mixins } from 'vue-class-component'
import { Component } from 'vue-property-decorator'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { SnackType } from '@/interfaces/snack'
import { LIACase } from '@/modules/LifeInsure/interfaces/life-insure'
import { File } from '@/modules/File/interfaces/file'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors, showNotification } from '@/utils/snack'
import { dateTimeToDisplay, dateToDisplay } from '@/utils/dateTime'
import { fileSizeFormat } from '@/utils/fileSize'
import { translateFinancialTypeKey } from '@/utils/translate'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTextarea from '@/components/AppTextarea.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppLightModal from '@/components/AppLightModal.vue'
import AppRadio from '@/components/AppRadio.vue'
import AppFileUploader from '@/components/AppFileUploader.vue'
import AppPhoneInput from '@/components/AppPhoneInput.vue'
import S3Handler from '@/mixins/s3'
import { getLIAById, deleteLIAFile, estimateLIAFile, addLIAFileOpinion, editLIA, deleteLIAById, applyLIA, withdrawLIA } from '@/modules/LifeInsure/gql/life-insure.gql'
import { formatPhoneNumber } from '@/utils/phoneNumberFormat'
import delNonNumCode from '@/utils/delNonNumCode'

interface BasicInfo {
  title: string
  content: string | number
}

@Component({
  components: {
    AppBreadcrumbs,
    AppDialog,
    AppTextField,
    AppTextarea,
    AppSelector,
    AppDatePicker,
    AppLightModal,
    AppRadio,
    AppFileUploader,
    AppPhoneInput
  }
})
export default class LifeInsureCaseDetail extends mixins(S3Handler) {
  windowSize = {
    w: 0,
    h: 0
  }

  caseReady = false

  caseId = 0

  liaCase: LIACase = {
    id: 0,
    name: '',
    birthday: '',
    memo: '',
    gender: '',
    applied: false,
    phone: '',
    applicantName: '',
    files: [],
    createdAt: -1,
    countryCode: '',
    extensionNumber: '',
    countryCallingCode: ''
  }

  liaCaseInput = {
    id: 0,
    name: '',
    birthday: '',
    memo: '',
    gender: -1,
    applied: false,
    phoneSplice: '',
    applicantName: '',
    files: [],
    createdAt: -1
  }

  breadcrumbs: Breadcrumb[] = [
    {
      text: `Life Insure Case`,
      to: { name: 'LifeInsureCaseList' }
    }
  ]

  basicInfo: BasicInfo[] = []

  filesGrouped: {
    [key: string]: File[]
  } = {}

  fileKeys: {
    type: string
    text: string
  }[] = [
    {
      type: 'APPLICATION_FORM',
      text: '申请表'
    },
    {
      type: 'FINANCIAL_PROOF',
      text: '财务证明'
    },
    {
      type: 'EXAMINATION_TABLE',
      text: '体检表'
    },
    {
      type: 'BANK_CHEQUE',
      text: '银行支票'
    },
    {
      type: 'AMERICAN_RELATIONS_PROOF',
      text: '美国关系证明'
    },
    {
      type: 'OTHER',
      text: 'Other Information'
    }
  ]

  newCaseValid = true
  editCaseDialog = false
  disableConfirm = false

  deleteDialog = false
  applyDialog = false
  withdrawDialog = false

  fileType = ''
  isSelectFile = false

  deleteFileDialog = false
  fileToDelete: File | null = null

  filesLoading = false

  fileHeaders = [
    { text: 'Name', value: 'name', sortable: false, width: 300 },
    { text: 'Upload Time & File Size', value: 'size', sortable: false, width: 250 },
    { text: 'Notes', value: 'notes', sortable: false },
    { text: 'Opinion', value: 'opinion', sortable: false, width: 250 },
    { text: 'Actions', value: 'actions', sortable: false, width: 140 }
  ]

  files = []

  estimateDialog = false
  fileToEstimate: File | null = null
  estimateStatus = false

  opinionDialog = false
  fileToOpinion: File & { opinionTemp: string } | null = null
  opinionStatus = false

  genderOptions = ['Male', 'Female', 'Other']

  btnLoading = false

  translateFinancialTypeKey = (type: string) => translateFinancialTypeKey(type)

  mounted () {
    this.onResize()
    this.caseId = Number(this.$route.params.id)
    this.breadcrumbs.push({
      text: this.caseId
    })
    this.getLIAbyId()
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
  }

  async getLIAbyId () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getLIAById,
        variables: {
          liaId: Number(this.caseId)
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.liaCase = Object.assign({}, result.data.getLIAById)
      this.liaCaseInput = Object.assign({}, result.data.getLIAById, {
        gender: this.$R.findIndex((gender: string) => {
          return gender === result.data.getLIAById.gender
        }, this.genderOptions)
      })
      this.liaCaseInput.phoneSplice = (result.data.getLIAById.countryCode || '') + ',' + (result.data.getLIAById.phone || '') + ',' + (result.data.getLIAById.extensionNumber || '') + ',' + (result.data.getLIAById.countryCallingCode || '')
      this.updateCaseInfo()
      this.groupFiles()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  groupFiles () {
    const files = this.$R.map((file: any) => this.$R.merge({
      opinionShow: false,
      estimateShow: false,
      opinionToolTipShow: false,
      nameShow: false,
      notesShow: false
    }, file), this.liaCase.files)

    this.filesGrouped = this.$R.groupBy((file: File) => {
      return file.type
    })(files)
  }

  updateCaseInfo () {
    const { id, createdAt, gender, birthday, phone, applicantName, countryCode, extensionNumber } = this.liaCase
    this.basicInfo = [
      {
        title: 'CaseID',
        content: id
      },
      {
        title: 'Creation Time',
        content: dateTimeToDisplay(createdAt)
      },
      {
        title: 'Gender',
        content: gender
      },
      {
        title: 'Birthday',
        content: dateToDisplay(birthday)
      },
      {
        title: 'Phone',
        content: phone ? formatPhoneNumber(phone, countryCode, extensionNumber) : '-'
      },
      {
        title: 'Agent',
        content: applicantName
      }
    ]
    this.caseReady = true
  }

  formatFileInfo (file: File) {
    const { updatedAt, size, name } = file
    return dateTimeToDisplay(updatedAt) + ' ' + fileSizeFormat(Number(size))
  }

  deleteCase () {
    this.deleteDialog = true
  }

  async editCaseConfirm () {
    const editCaseValid = (this.$refs.form as Vue & { validate: () => boolean }).validate()
    if (!editCaseValid) {
      return
    }

    const gender = this.genderOptions[this.liaCaseInput.gender]

    const phoneList = this.liaCaseInput.phoneSplice.split(',')
    const countryCode = phoneList[0] ? phoneList[0] : ''
    const phone = phoneList[1] ? phoneList[1] : ''
    const extensionNumber = phoneList[2] ? phoneList[2] : ''
    const countryCallingCode = phoneList[3] ? phoneList[3] : ''

    if (!phone) {
      return
    }

    this.disableConfirm = true

    try {
      const result = await this.$apollo.mutate({
        mutation: editLIA,
        variables: {
          liaId: Number(this.caseId),
          liaInput: {
            name: this.liaCaseInput.name,
            birthday: this.liaCaseInput.birthday + '',
            phone: delNonNumCode(phone),
            gender: gender,
            memo: this.liaCaseInput.memo,
            countryCode,
            extensionNumber,
            countryCallingCode
          }
        }
      })
      showNotification({
        msg: 'Case edited successfully',
        type: SnackType.Success
      })
      this.liaCase = Object.assign({}, result.data.editLIA)
      this.updateCaseInfo()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
    this.editCaseDialog = false
    this.disableConfirm = false
  }

  async deleteCaseConfirm () {
    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: deleteLIAById,
        variables: {
          liaId: Number(this.caseId)
        }
      })
      const res = result.data.deleteLIAById
      if (res) {
        showNotification({
          msg: 'Case deleted successfully',
          type: SnackType.Success
        })
        this.$router.replace({
          name: 'LifeInsureCaseList'
        })
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
    this.disableConfirm = false
  }

  async applyLIA () {
    this.applyDialog = true
  }

  async applyCaseConfirm () {
    try {
      const result = await this.$apollo.mutate({
        mutation: applyLIA,
        variables: {
          liaId: Number(this.caseId)
        }
      })
      this.applyDialog = false
      const res = result.data.applyLIA
      if (res) {
        showNotification({
          msg: 'Apply this case success!',
          type: SnackType.Success
        })
      }
      this.getLIAbyId()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async withdrawLIA () {
    this.withdrawDialog = true
  }

  async withdrawCaseConfirm () {
    try {
      const result = await this.$apollo.mutate({
        mutation: withdrawLIA,
        variables: {
          liaId: Number(this.caseId)
        }
      })
      this.withdrawDialog = false
      const res = result.data.withdrawLIA
      if (res) {
        showNotification({
          msg: 'Withdraw this case success!',
          type: SnackType.Success
        })
        this.getLIAbyId()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  editCase () {
    this.editCaseDialog = true
  }

  addFile (fileType: string) {
    this.fileType = fileType
    this.isSelectFile = true
  }

  deleteFile (file: File) {
    this.fileToDelete = file
    this.deleteFileDialog = true
  }

  async deleteFileConfirm () {
    if (!this.fileToDelete) {
      return
    }
    const { id } = this.fileToDelete
    try {
      const result = await this.$apollo.mutate({
        mutation: deleteLIAFile,
        variables: {
          fileId: Number(id)
        }
      })
      this.deleteFileDialog = false
      const res = result.data.deleteLIAFile
      if (res) {
        showNotification({
          msg: 'File deleted success!',
          type: SnackType.Success
        })
        this.getLIAbyId()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  estimateFile (status: boolean, file: File & { opinionShow: boolean, estimateShow: boolean }) {
    this.fileToEstimate = file
    this.estimateStatus = status
    this.estimateDialog = true
  }

  async estimateConfirm () {
    if (!this.fileToEstimate) {
      return
    }
    const { id } = this.fileToEstimate
    try {
      const result = await this.$apollo.mutate({
        mutation: estimateLIAFile,
        variables: {
          fileId: Number(id),
          status: this.estimateStatus
        }
      })
      this.estimateDialog = false
      const res = result.data.estimateLIAFile
      if (res) {
        showNotification({
          msg: 'File estimate success!',
          type: SnackType.Success
        })
        this.getLIAbyId()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  opinionFile (action: boolean, file: any) {
    if (action) {
      this.fileToOpinion = file
      this.opinionDialog = true
    } else {
      file.opinionToolTipShow = false
    }
  }

  async opinionConfirm () {
    if (!this.fileToOpinion) {
      return
    }
    const { id } = this.fileToOpinion
    try {
      const result = await this.$apollo.mutate({
        mutation: addLIAFileOpinion,
        variables: {
          fileId: Number(id),
          opinion: this.fileToOpinion.opinionTemp
        }
      })
      this.opinionDialog = false
      const res = result.data.addLIAFileOpinion
      if (res) {
        showNotification({
          msg: 'Add file opinion success!',
          type: SnackType.Success
        })
        this.getLIAbyId()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  uploadFileSuccess () {
    this.isSelectFile = false
    this.getLIAbyId()
  }

  async downloadFileHandler (file: any) {
    this.btnLoading = true
    await this.downloadFile(file)
    this.btnLoading = false
  }
}
